import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { useIntl } from 'react-intl';
import type { OperationResult } from 'urql';
import type {
  Exact,
  UnsubscribeFlexikontoMutation,
  UnsubscribeFromNewsletterMutation,
} from '@packages/gql/generated/shopping/graphql';
import type {
  GTMEventGlycerinDisplayError,
  GTMEventGlycerinFormSubmitClick,
  GTMEventGlycerinUnsubscribe,
} from '@packages/tracking/src/types/events';
import { GlycerinErrorSeverity } from '@packages/tracking/src/types/enums';
import type { Dispatch, SetStateAction } from 'react';
import { useSnackbar } from '@packages/shared/src/components/Notification/SnackbarContext';
import { useExecuteUnsubscribe } from './useExecuteUnsubscribe';
import { generalFormMessages } from '../../messages';
import type { FormSubmitResult } from '../../types';

export const useSubmitForm = ({
  setFormSubmitResult,
  areaKey,
}: {
  setFormSubmitResult: Dispatch<SetStateAction<FormSubmitResult | null>>;
  areaKey: string;
  setError: any;
}) => {
  const { formatMessage } = useIntl();
  const dispatchGTMEvent = useTracking();
  const executeUnsubscribe = useExecuteUnsubscribe();
  const { addToast } = useSnackbar();
  const submitForm = async (data: { frequency: string; email: any }) => {
    await executeUnsubscribe(data)
      .then(
        (
          response: OperationResult<
            UnsubscribeFromNewsletterMutation | UnsubscribeFlexikontoMutation,
            Exact<{
              emailAddress: string;
            }>
          >,
        ) => {
          if (response.error) {
            throw new Error(formatMessage(generalFormMessages.errorApi));
          }

          // if no errors
          let resultType: FormSubmitResult['resultType'];
          switch (data.frequency?.split('|')[0]) {
            case 'frequency':
              resultType = 'FREQUENCY_SUCCESSFUL';
              break;
            case 'pause':
              resultType = 'PAUSE_SUCCESSFUL';
              break;
            default:
              resultType = 'UNSUBSCRIBE_SUCCESSFUL';
          }
          setFormSubmitResult({ resultType, successful: true });
          dispatchGTMEvent({
            event: 'ga_event',
            eventCategory: 'User Interactions',
            eventLabel: 'newsletter',
            eventAction: 'unsubscribe',
          });
          dispatchGTMEvent<GTMEventGlycerinFormSubmitClick>({
            event: 'FormSubmitClick',
            FormSubmitClickData: {
              category: 'newsletter-unsubscribe',
              label: 'registration',
              detail: 'send',
              custom: { areaKey },
            },
          });
          if (data.email && data.frequency?.split('|')[0] === 'unsubscribe') {
            dispatchGTMEvent<GTMEventGlycerinUnsubscribe>({
              event: 'Unsubscribe',
              UnsubscribeData: {
                email: data?.email,
                custom: { areaKey },
              },
            });
          }
        },
      )
      .catch((error: Error) => {
        addToast({ message: error.toString(), severity: 'error', duration: 2500 });
        dispatchGTMEvent<GTMEventGlycerinDisplayError>({
          event: 'DisplayError',
          DisplayErrorData: {
            category: 'newsletter-unsubscribe',
            label: 'registration',
            detail: 'error',
            errors: [
              {
                errorType: 'formValidation',
                message: error.toString(),
                severity: GlycerinErrorSeverity.WARNING,
                source: 'newsletter unsubscribe form',
              },
            ],
          },
        });
      });
  };
  return submitForm;
};
